import React, { useEffect, useRef, useState, useMemo } from "react";
import format from "date-fns/format";
import API, { showNetworkErrorToast } from "Utils/api";
import { scrollToShowDropdown } from "../../../../utils/dropdownScrollUtils";
import {
  VersionDropdownContainer,
  VersionDropdownContent,
  VersionDropdownIcon,
  VersionDropdownLabel,
  VersionDropdownOption,
  VersionDropdownOptions,
  VersionDropdownValue,
  VersionDropdownValueText,
} from "../styles";
import { ReactComponent as DropdownDownIconSvg } from "../assets/dropdown-iterative-down.svg";
import { ReactComponent as DropdownUpIconSvg } from "../assets/dropdown-iterative-up.svg";
import { FMSLogger } from "FMSLogger";

const logger = FMSLogger.byPrefix("VersionSelector");

interface VersionSelectorProps {
  missionId: string;
  onSelect: (versionId: string) => void;
  disabled?: boolean;
}

// Extracted functions for testing
export const sortVersionsNewestFirst = (
  versions: MissionVersion[]
): MissionVersion[] => {
  return [...versions].sort((a, b) => b.version_number - a.version_number);
};

export const formatVersionDisplay = (version: MissionVersion): string => {
  const dateStr = format(new Date(version.created_at), "MMM d, yyyy");
  return `v${version.version_number} - ${dateStr}`;
};

const VersionSelector: React.FC<VersionSelectorProps> = ({
  missionId,
  onSelect,
  disabled = false,
}) => {
  const [versions, setVersions] = useState<MissionVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sort versions newest first
  const sortedVersions = useMemo(() => {
    return sortVersionsNewestFirst(versions);
  }, [versions]);

  // Fetch versions when missionId changes
  useEffect(() => {
    const fetchVersions = async () => {
      if (!missionId || disabled) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        logger.debug(`Fetching versions for mission: ${missionId}`);
        const response = await API.get(`/missions/${missionId}/versions/`);
        const fetchedVersions = response.data as MissionVersion[];
        setVersions(fetchedVersions);
        logger.debug(`Fetched ${fetchedVersions.length} versions`);
      } catch (err: any) {
        logger.error("Failed to fetch versions", err);
        setError("Failed to load versions");
        showNetworkErrorToast(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVersions();
  }, [missionId, disabled]);

  // Auto-select latest version
  useEffect(() => {
    if (sortedVersions.length > 0 && !selectedVersionId) {
      const latest = sortedVersions[0];
      setSelectedVersionId(latest.id);
      onSelect(latest.id);
      logger.debug(`Auto-selected latest version: v${latest.version_number}`);
    }
  }, [sortedVersions, selectedVersionId, onSelect]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled && !isLoading && versions.length > 0) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        // Scroll to show dropdown if needed
        setTimeout(() => {
          scrollToShowDropdown(dropdownRef);
        }, 100);
      }
    }
  };

  const handleVersionSelect = (version: MissionVersion) => {
    if (!disabled) {
      setSelectedVersionId(version.id);
      onSelect(version.id);
      setIsOpen(false);
      logger.debug(`Selected version: v${version.version_number}`);
    }
  };

  const getDisplayText = (): string => {
    if (isLoading) {
      return "Loading versions...";
    }
    if (error) {
      return "Error loading versions";
    }
    if (versions.length === 0) {
      return "No versions available";
    }
    if (selectedVersionId) {
      const selectedVersion = versions.find((v) => v.id === selectedVersionId);
      if (selectedVersion) {
        return formatVersionDisplay(selectedVersion);
      }
    }
    return "Select version";
  };

  return (
    <VersionDropdownContainer
      ref={dropdownRef}
      style={disabled ? { opacity: 0.5, backgroundColor: "#f5f5f5" } : {}}
    >
      <VersionDropdownContent onClick={handleToggle}>
        <VersionDropdownLabel>Version</VersionDropdownLabel>
        <VersionDropdownValue>
          <VersionDropdownValueText>
            {getDisplayText()}
          </VersionDropdownValueText>
        </VersionDropdownValue>
      </VersionDropdownContent>
      {!disabled && !isLoading && versions.length > 0 && (
        <VersionDropdownIcon onClick={handleToggle}>
          {isOpen ? <DropdownUpIconSvg /> : <DropdownDownIconSvg />}
        </VersionDropdownIcon>
      )}

      {isOpen && !disabled && versions.length > 0 && (
        <VersionDropdownOptions data-dropdown-options>
          {sortedVersions.map((version: MissionVersion) => (
            <VersionDropdownOption
              key={version.id}
              onClick={() => handleVersionSelect(version)}
            >
              {formatVersionDisplay(version)}
            </VersionDropdownOption>
          ))}
        </VersionDropdownOptions>
      )}
    </VersionDropdownContainer>
  );
};

export default VersionSelector;
